import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { LocalDateTime } from "../messages/JSJodaJSON";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { CHECK_OUTLINED_ICON } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { AppIcon } from "./Generic/AppIcon";
import Flex from "./Generic/Flex";
import { isDefined } from "./Generic/IsDefined";

interface Props {
  canClockIn: boolean;
  canClockOut: boolean;
  clockInDateTime: LocalDateTime | null;
  clockOutDateTime: LocalDateTime | null;
  onClockIn: () => void;
  onClockOut: () => void;
}

const MedflytClockInOutFooter = (props: Props) => {
  const checkIcon = <AppIcon icon={CHECK_OUTLINED_ICON} size={18} />;

  return (
    <div>
      <Flex xGap={8} alignItems="center" stretchItems={true}>
        <Flex stretchItems={true}>
          {!isDefined(props.clockInDateTime) ? (
            <ClockButton
              type="IN"
              isActive={props.canClockIn}
              showAnimation={props.canClockIn}
              onClick={props.onClockIn}
            />
          ) : (
            <MedflytClockInOutFooter.SuceessButton variant="soft" color="success-dark">
              {checkIcon}
              {`${globalize().formatLocalDateTime(props.clockInDateTime, { time: "short" })}`}
            </MedflytClockInOutFooter.SuceessButton>
          )}
        </Flex>
        <Flex stretchItems={true}>
          {!isDefined(props.clockOutDateTime) ? (
            <ClockButton
              type="OUT"
              isActive={props.canClockOut}
              showAnimation={!props.canClockIn && props.canClockOut}
              onClick={props.onClockOut}
            />
          ) : (
            <MedflytClockInOutFooter.SuceessButton variant="soft" color="success-dark">
              {checkIcon}
              {`${globalize().formatLocalDateTime(props.clockOutDateTime, { time: "short" })}`}
            </MedflytClockInOutFooter.SuceessButton>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

const ClockButton = (props: {
  showAnimation: boolean;
  isActive: boolean;
  type: "IN" | "OUT";
  onClick: () => void;
}) => {
  const { label } = {
    IN: { label: translate(STRINGS.CLOCK_IN) },
    OUT: { label: translate(STRINGS.CLOCK_OUT) },
  }[props.type];

  return (
    <MedflytClockInOutFooter.ClockButton
      data-animate={props.showAnimation}
      data-disabled={!props.isActive}
      onClick={props.onClick}
    >
      <span>{label}</span>
    </MedflytClockInOutFooter.ClockButton>
  );
};

const shadow = keyframes`
    0% { transform: none; opacity: 1;  }
    100% { transform: scaleY(1.8) scaleX(1.12); opacity: 0; }
`;

const gradient = keyframes`
0% {
    background-position: 0% 50%;
}
50% {
    background-position: 100% 50%;
}
100% {
    background-position: 0% 50%;
}
`;

MedflytClockInOutFooter.SuceessButton = styled(AppButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

MedflytClockInOutFooter.ClockButton = styled(AppButton)<{
  ["data-animate"]: boolean;
  ["data-disabled"]: boolean;
}>`
  background: var(--primary);
  background-size: 400% 400%;
  animation: ${gradient} 10s ease infinite;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: unset;
    animation: ${gradient} 10s ease infinite, ${shadow} 2s ease infinite;
    z-index: -1;
    border-radius: 8px;
    transform: scaleY(1.4) scaleX(1.06);
  }

  &[data-animate="true"]:before {
    background: var(--primary);
  }

  &[data-disabled="true"] {
    opacity: 0.5;
  }
`;

export default MedflytClockInOutFooter;
